/**
 * ログインコマンド
 */

import type { ArgumentsCamelCase, Argv } from 'yargs';
import { loadConfig, saveConfig, ENV_KEYS, DEFAULT_BASE_URL } from '../lib/config-store.js';
import * as readline from 'readline';
import type { HelpOption } from '../lib/help-schema.js';

interface LoginArgs {
  baseUrl?: string;
  tenantId?: string;
  accessToken?: string;
}

export function command(): string {
  return 'login';
}

export function describe(): string {
  return 'acomo にログインします';
}

/** --help 用のオプション一覧（共通 help JSON 用） */
export const helpOptions: HelpOption[] = [
  { name: 'base-url', type: 'string', description: `baseUrl (env: ${ENV_KEYS.baseUrl})` },
  { name: 'tenant-id', type: 'string', description: `tenantId (env: ${ENV_KEYS.tenantId})` },
  { name: 'access-token', type: 'string', description: `accessToken (env: ${ENV_KEYS.accessToken})` },
];

export function builder(yargs: Argv): Argv {
  return yargs
    .option('base-url', {
      type: 'string',
      description: `baseUrl (env: ${ENV_KEYS.baseUrl})`,
    })
    .option('tenant-id', {
      type: 'string',
      description: `tenantId (env: ${ENV_KEYS.tenantId})`,
      default: process.env[ENV_KEYS.tenantId],
    })
    .option('access-token', {
      type: 'string',
      description: `accessToken (env: ${ENV_KEYS.accessToken})`,
      default: process.env[ENV_KEYS.accessToken],
    });
}

/**
 * インタラクティブに入力を取得（デフォルト値ありの場合は Enter でそのまま採用）
 * @param displayDefault - プロンプトに表示する文字列（省略時は defaultValue をそのまま表示。accessToken のマスク用）
 */
async function promptInput(question: string, defaultValue?: string, displayDefault?: string): Promise<string> {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });
  const shown = displayDefault !== undefined ? displayDefault : defaultValue;
  const prompt = shown !== undefined && shown !== ''
    ? `${question} [${shown}]: `
    : `${question}: `;
  return new Promise((resolve) => {
    rl.question(prompt, (answer) => {
      rl.close();
      const trimmed = answer.trim();
      resolve(trimmed !== '' ? trimmed : (defaultValue ?? ''));
    });
  });
}

/** accessToken を config show と同様に末尾省略して表示用にマスク */
function maskAccessToken(token: string): string {
  return token && token.length > 10 ? `${token.slice(0, 10)}...` : token;
}

export async function handler(argv: ArgumentsCamelCase<LoginArgs>): Promise<void> {
  try {
    const existing = loadConfig();

    const defaultBaseUrl = argv.baseUrl ?? existing?.baseUrl ?? process.env[ENV_KEYS.baseUrl] ?? DEFAULT_BASE_URL;
    const defaultTenantId = argv.tenantId ?? existing?.tenantId ?? process.env[ENV_KEYS.tenantId] ?? '';
    const defaultAccessToken = argv.accessToken ?? existing?.accessToken ?? process.env[ENV_KEYS.accessToken] ?? '';

    let baseUrl: string;
    let tenantId: string;
    let accessToken: string;

    if (process.stdin.isTTY) {
      baseUrl = (await promptInput('baseUrl', defaultBaseUrl)).trim() || defaultBaseUrl;
      tenantId = (await promptInput('tenantId', defaultTenantId)).trim();
      accessToken = (await promptInput('accessToken', defaultAccessToken, maskAccessToken(defaultAccessToken))).trim();
    } else {
      baseUrl = defaultBaseUrl;
      tenantId = defaultTenantId;
      accessToken = defaultAccessToken;
    }

    if (!tenantId) {
      console.error(
        `tenantId が指定されていません。--tenant-id オプションまたは環境変数 ${ENV_KEYS.tenantId} を設定してください。`,
      );
      process.exit(1);
    }
    if (!accessToken) {
      console.error(
        '非インタラクティブ環境では --access-token と --tenant-id（または環境変数）の指定が必要です。\n' +
          'acomo login --tenant-id <id> --access-token <accessToken>\n' +
          `または環境変数 ${ENV_KEYS.accessToken}, ${ENV_KEYS.tenantId} を設定してください。`,
      );
      process.exit(1);
    }

    saveConfig({
      baseUrl: baseUrl || DEFAULT_BASE_URL,
      tenantId,
      accessToken,
    });

    console.log('ログイン情報を設定しました。');
  } catch (error: any) {
    console.error('ログインに失敗しました:', error.message);
    process.exit(1);
  }
}
